import { lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  LayoutDashboard,
  Palette,
  Server,
  Settings2,
  Smartphone,
  Sparkles,
  Users,
} from 'lucide-react';
import { AccountPage, Alert, AppShell, LoginPage, RequireAuth, type NavItem } from '@iptv/ui';
import { useOverview } from './queries.js';

const DashboardPage = lazy(() =>
  import('./pages/DashboardPage.js').then((m) => ({ default: m.DashboardPage })),
);
const HostsPage = lazy(() =>
  import('./pages/HostsPage.js').then((m) => ({ default: m.HostsPage })),
);
const BrandingPage = lazy(() =>
  import('./pages/BrandingPage.js').then((m) => ({ default: m.BrandingPage })),
);
const FeaturedPage = lazy(() =>
  import('./pages/FeaturedPage.js').then((m) => ({ default: m.FeaturedPage })),
);
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage.js').then((m) => ({ default: m.SettingsPage })),
);
const DevicesPage = lazy(() =>
  import('./pages/DevicesPage.js').then((m) => ({ default: m.DevicesPage })),
);
const UsersPage = lazy(() =>
  import('./pages/UsersPage.js').then((m) => ({ default: m.UsersPage })),
);
const AuditPage = lazy(() =>
  import('./pages/AuditPage.js').then((m) => ({ default: m.AuditPage })),
);

function TenantBanner() {
  const { t } = useTranslation();
  const { data } = useOverview();
  if (!data || data.status === 'ACTIVE') return null;
  return (
    <div className="mx-auto w-full max-w-[1240px] px-4 pt-2 sm:px-6 lg:px-8">
      <Alert tone="danger">
        {t(data.status === 'SUSPENDED' ? 'errors.TENANT_SUSPENDED' : 'errors.ACCOUNT_DISABLED')}
      </Alert>
    </div>
  );
}

function Shell() {
  const { t } = useTranslation();
  const nav: NavItem[] = [
    {
      to: '/',
      label: t('nav.dashboard'),
      shortLabel: t('navShort.dashboard'),
      icon: LayoutDashboard,
      end: true,
      permission: 'tenant:read',
    },
    {
      to: '/hosts',
      label: t('nav.hosts'),
      shortLabel: t('navShort.hosts'),
      icon: Server,
      permission: 'hosts:read',
    },
    {
      to: '/branding',
      label: t('nav.branding'),
      shortLabel: t('navShort.branding'),
      icon: Palette,
      permission: 'branding:read',
    },
    {
      to: '/featured',
      label: t('nav.featured'),
      shortLabel: t('navShort.featured'),
      icon: Sparkles,
      permission: 'branding:read',
    },
    {
      to: '/settings',
      label: t('nav.settings'),
      shortLabel: t('navShort.settings'),
      icon: Settings2,
      permission: 'settings:read',
    },
    {
      to: '/devices',
      label: t('nav.devices'),
      shortLabel: t('navShort.devices'),
      icon: Smartphone,
      permission: 'devices:read',
    },
    {
      to: '/users',
      label: t('nav.users'),
      shortLabel: t('navShort.users'),
      icon: Users,
      permission: 'users:read',
    },
    {
      to: '/audit',
      label: t('nav.audit'),
      shortLabel: t('navShort.audit'),
      icon: Activity,
      permission: 'audit:read',
    },
  ];
  return (
    <RequireAuth>
      <AppShell title={t('common.appTitleMerchant')} nav={nav} banner={<TenantBanner />} />
    </RequireAuth>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<Shell />}>
          <Route index element={<DashboardPage />} />
          <Route path="hosts" element={<HostsPage />} />
          <Route path="branding" element={<BrandingPage />} />
          <Route path="featured" element={<FeaturedPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="devices" element={<DevicesPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="account" element={<AccountPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
